import { Item, Items } from '../Card';
import { Content, Header } from '../../libs/gs-component-right-rail';
import { compose } from '@xo-union/react-css-modules';
import { IconButton } from '../IconButton';
import { ToggleSwitch } from '@xo-union/tk-component-switches';
import gql from 'graphql-tag';
import Honeybadger from 'honeybadger-js';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';

import {
  PagesFragmentDoc,
  useDeletePageMutation,
  usePagesQuery,
  useUpdatePageMutation,
  useUpdatePagesMutation,
  Wws_UpdatePageAttributes,
  useRegistrySummaryQuery,
  PagesQuery,
} from '../../generated/graphql';
import { reorder } from '../../helpers/reorder';
import { TrackingContext } from '../../TrackingContext';
import draggableStyle from '../Item/index.scss';
import { pagesQuery } from '../LeftContainer/graphql';
import parentDragStyle from '../PageCard/index.scss';
import { deletePageUpdate } from './deletePageUpdate';
import styles from './reorderPages.scss';
import CVAlert from '../CVAlert';
import registryInfo from '../../helpers/registryInfo';
import useIsLiteSite from '../../helpers/useIsLiteSite';
import { pagesWithContent } from '../../helpers/pages';
import { WEDDING_PAGE_TYPE, LITE_SITE_PAGE_TYPE } from '../../helpers/pages';
import { useWebsiteLayoutContext } from '../../context/WebsiteLayoutContext';

interface Props {
  onClose: () => void;
  leftButtonOnClick?: () => void;
  updatePreview?: () => void;
}

export const updatePagesMutation = gql`
  mutation updatePagesMutation($pages: [WWS_UpdatePageAttributes!]!) {
    updatePages(pages: $pages) {
      ...Pages
    }
  }
  ${PagesFragmentDoc}
`;

export const ReorderPages: React.FC<Props> = ({ onClose, leftButtonOnClick, updatePreview = () => {} }) => {
  const liteSite = useIsLiteSite();
  const { currentLayout } = useWebsiteLayoutContext();
  const { data: registryData } = useRegistrySummaryQuery();
  const { hasAnyRegistry } = registryInfo(registryData?.registrySummary);
  const { data } = usePagesQuery();
  const pages = useMemo(() => (liteSite ? pagesWithContent(data?.pages || [], hasAnyRegistry) : data?.pages), [
    data,
    liteSite,
    hasAnyRegistry,
  ]);
  const [sortedItems, setSortedItems] = useState(pages || []);
  const [updatePage] = useUpdatePageMutation();
  const [deletePage] = useDeletePageMutation();
  const [updatePages] = useUpdatePagesMutation();
  const { track } = useContext(TrackingContext);
  const [erroring, setErroring] = useState(false);

  useEffect(() => {
    setSortedItems(pages || []);
  }, [pages]);

  if (!data) {
    return null;
  }

  const onDragEnd = async ({ source, destination }: DropResult) => {
    // prevent destinationIdx === 0 so no page can appear before the home page
    const destinationIdx = destination?.index === 0 ? 1 : destination?.index;
    const sorted = reorder(sortedItems, source.index, destinationIdx);
    setSortedItems(sorted);
    const updated = sorted.map(({ id, title, rank, show }) => ({
      id,
      title,
      rank,
      show,
    }));

    try {
      await updatePages({
        variables: {
          pages: updated as Wws_UpdatePageAttributes[],
        },
        update(cache, { data }) {
          const existingPages = cache.readQuery<PagesQuery>({ query: pagesQuery });

          const updatedPages = data?.updatePages;
          const updatedPagesMap = data?.updatePages.reduce((m: Map<String, String>, p) => {
            m.set(p.type, p.type);
            return m;
          }, new Map());

          existingPages?.pages.forEach(p => {
            if (updatedPagesMap?.get(p.type)) {
              return;
            }

            updatedPages?.push(p);
          });

          cache.writeQuery({
            query: pagesQuery,
            data: {
              pages: updatedPages,
            },
          });
          updatePreview();
        },
      });
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error sorting pages',
        context: {
          error,
        },
      });
      setSortedItems(data.pages);
      setErroring(true);
    }
  };

  const onDelete = async (id: number) => {
    try {
      await deletePage({
        variables: { id },
        update(cache, { data }) {
          deletePageUpdate(cache, data);
        },
      });
      updatePreview();
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error deleting page',
        context: {
          error,
        },
      });
      setErroring(true);
    }
  };

  const isSinglePageSite = currentLayout === 'single-page';
  const pagesOrSections = isSinglePageSite ? 'sections' : 'pages';

  return (
    <>
      <Header
        leftButtonText={leftButtonOnClick !== undefined ? 'Back' : ''}
        leftButtonOnClick={leftButtonOnClick}
        onClose={onClose}
        as="h1"
      >
        {`Reorder ${pagesOrSections}`}
      </Header>
      <Content className={styles.content}>
        <CVAlert erroring={erroring} onTimeout={() => setErroring(false)} />
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable-1" type="PAGES">
            {provided => (
              <Items
                innerRef={provided.innerRef}
                className={parentDragStyle.draggablePlaceholder}
                {...provided.droppableProps}
              >
                {sortedItems.map((page, index) => (
                  <Draggable
                    draggableId={`draggable-${page.id}`}
                    key={`draggable-${page.id}`}
                    index={index}
                    isDragDisabled={page.type === WEDDING_PAGE_TYPE || page.type === LITE_SITE_PAGE_TYPE}
                  >
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <Item
                          showGrabber={page.type !== WEDDING_PAGE_TYPE && page.type !== LITE_SITE_PAGE_TYPE}
                          className={snapshot.isDragging ? draggableStyle.item : ''}
                          rightElement={
                            <>
                              {page.type !== WEDDING_PAGE_TYPE && page.type !== LITE_SITE_PAGE_TYPE && (
                                <ToggleSwitch
                                  classes={compose({ container: styles.switchContainer })}
                                  data-testid={`${page.title}-visible`}
                                  checked={page.show}
                                  labelDirection="left"
                                  onChange={async () => {
                                    try {
                                      await updatePage({
                                        variables: {
                                          id: page.id as number,
                                          attributes: { show: !page.show },
                                        },
                                        update(_cache, { data }) {
                                          track('WWS Page Edited', {
                                            pageType: data?.updatePage?.type,
                                            action: 'edit',
                                            actionName: 'visibility',
                                            pageVisible: data?.updatePage?.show,
                                          });
                                          updatePreview();
                                        },
                                      });
                                    } catch (error) {
                                      Honeybadger.notify(error, {
                                        message: 'Error updating page',
                                        context: {
                                          error,
                                        },
                                      });
                                      setErroring(true);
                                    }
                                  }}
                                >
                                  {page.show ? 'Visible' : 'Hidden'}
                                </ToggleSwitch>
                              )}
                            </>
                          }
                        >
                          <div className={styles.toggle}>
                            <div>
                              <span>{page.type !== WEDDING_PAGE_TYPE ? page.title : 'Home'}</span>
                              {page.type === 'CustomPage' && (
                                <IconButton
                                  data-testid="delete-page"
                                  size="sm"
                                  datatestid="reorder-delete-page"
                                  arialabel="delete-page"
                                  name="delete"
                                  onClick={() => onDelete(page.id as number)}
                                />
                              )}
                            </div>
                          </div>
                        </Item>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Items>
            )}
          </Droppable>
        </DragDropContext>
      </Content>
    </>
  );
};
